import { useEffect } from "react";
import { X } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";

/**
 * BottomSheet - Mobile-first drawer that slides up from bottom on mobile,
 * shows as centered modal on desktop (>= md).
 *
 * Usage:
 *   <BottomSheet open={!!selected} onClose={() => setSelected(null)} title="Ordine #123">
 *     <div>content...</div>
 *   </BottomSheet>
 */
export default function BottomSheet({ open, onClose, title, subtitle, children, testId, footer }) {
  const { t } = useI18n();
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="tf-sheet-backdrop" onClick={onClose} data-testid={testId || "bottom-sheet"}>
      <div className="tf-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="tf-sheet-handle" />
        <div className="tf-sheet-header">
          <div className="min-w-0 flex-1">
            {subtitle && <div className="tf-label">{subtitle}</div>}
            {title && (
              <h2 className="font-display font-black text-xl md:text-2xl mt-1 truncate">
                {title}
              </h2>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--tf-bg)] transition flex-shrink-0"
            aria-label={t("close_aria")}
            data-testid="sheet-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="tf-sheet-body">{children}</div>
        {footer && (
          <div className="p-4 border-t border-[var(--tf-border)] flex-shrink-0" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1rem)" }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
